import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "./tours.scss";
import i18n from "../i18n";
import { TOURS, type TourId } from "./tourDefinitions";

const SEEN_KEY = 'sengi_tours_seen';

const readSeen = (): string[] => {
    try {
        const raw = localStorage.getItem(SEEN_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const isTourSeen = (id: TourId) => readSeen().includes(id);

const markTourSeen = (id: TourId) => {
    const seen = readSeen();
    if (!seen.includes(id)) localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, id]));
};

// "Sichtbar" = gerendert UND zumindest teilweise im Viewport. Das Mobile-Menue wird per
// translateX(-100%) aus dem Bild geschoben, hat dabei aber weiter Masse - ohne Viewport-Pruefung
// zeigte die Tour dort auf Elemente ausserhalb des Bildschirms.
const isVisible = (selector: string) => {
    const el = document.querySelector(selector);
    if (!el || el.getClientRects().length === 0 || getComputedStyle(el).visibility === 'hidden') return false;
    const r = el.getBoundingClientRect();
    return r.right > 0 && r.bottom > 0 && r.left < window.innerWidth && r.top < window.innerHeight;
};

let activeTour: ReturnType<typeof driver> | null = null;
export const isTourActive = () => activeTour !== null;

// Zaehler, der bei jedem Tour-Ende hochgeht - TourHost reagiert darauf und startet ggf. die naechste
// Tour (z. B. direkt nach der Willkommens-Tour die Chat-Tour), ohne auf einen Seitenwechsel zu warten.
let tourRevision = 0;
const tourListeners = new Set<() => void>();
export const subscribeTours = (listener: () => void) => {
    tourListeners.add(listener);
    return () => { tourListeners.delete(listener); };
};
export const getTourRevision = () => tourRevision;

// Wer eine Tour per X/Esc wegklickt, will gerade keine weitere - automatische Touren pausieren dann
// fuer den Rest der Sitzung (Neuladen setzt zurueck). Manuell ueber das Profilmenue geht es immer.
let autoToursSuppressed = false;
export const areAutoToursSuppressed = () => autoToursSuppressed;

// Startet eine Tour; Schritte ohne sichtbaren Anker entfallen. Bleibt nichts Sinnvolles uebrig,
// startet nichts (gilt dann trotzdem als gesehen, damit sie nicht bei jedem Besuch erneut prueft).
export const startTour = (id: TourId): boolean => {
    if (activeTour) return false;
    const t = (key: string) => i18n.t(key, { ns: 'tours' });

    const steps: DriveStep[] = TOURS[id]
        .filter(step => !step.element || isVisible(step.element))
        .map(step => ({
            element: step.element,
            popover: {
                title: t(`${id}.${step.key}.title`),
                description: t(`${id}.${step.key}.body`),
                side: step.side,
            },
        }));

    if (steps.length === 0) {
        markTourSeen(id);
        return false;
    }
    // Nur noch Einfuehrungstext ohne einen einzigen sichtbaren Anker (z. B. ImageStudio auf Mobile,
    // wo nur der Desktop-Hinweis steht): nicht starten und NICHT als gesehen markieren - beim
    // naechsten Besuch am Desktop soll die Tour normal kommen.
    if (TOURS[id].some(step => step.element) && !steps.some(step => step.element)) {
        return false;
    }

    let finished = false;
    const finish = (dismissed: boolean) => {
        if (finished) return;
        finished = true;
        if (dismissed) autoToursSuppressed = true;
        markTourSeen(id);
        activeTour = null;
        tourRevision += 1;
        tourListeners.forEach(listener => listener());
    };

    const tour = driver({
        steps,
        showProgress: steps.length > 1,
        // driver.js ersetzt {{current}}/{{total}} selbst - i18next wuerde die Platzhalter sonst leeren.
        progressText: i18n.t('progress', { ns: 'tours', current: '{{current}}', total: '{{total}}' }),
        nextBtnText: t('next'),
        prevBtnText: t('prev'),
        doneBtnText: t('done'),
        popoverClass: 'sengi-tour',
        // Auch "Schliessen"/Esc zaehlt als gesehen - niemand soll die Tour erzwungen doppelt sehen.
        // onDestroyStarted statt onDestroyed: driver.js ruft onDestroyed nur bei aktivem Element auf
        // und liess es beim Schliessen im ersten (anker-losen) Schritt aus - live gesehen 2026-09-21.
        // Der Hook uebernimmt das Beenden, daher destroy() hier selbst aufrufen.
        // Ohne weiteren Schritt = regulaer mit "Fertig" beendet; sonst weggeklickt.
        onDestroyStarted: () => {
            finish(tour.hasNextStep());
            tour.destroy();
        },
        onDestroyed: () => finish(false),
    });
    activeTour = tour;
    tour.drive();
    return true;
};
